import { api } from './apiClient';
import { API_BASE_URL } from '../config/api';

// Trade Portal catalogue registry. Mirrors the `catalogues` table + derived counts.
export interface Catalogue {
  id: string;
  brand: string;
  name: string;
  description: string;
  url: string;
  size_mb: number;
  sort_order: number;
  cover_url: string | null;
  added_by: string;
  created_at: string;
  updated_at: string;
  position_count?: number; // total product positions mapped
  mapped_pages?: number;   // distinct pages with at least one position
}

export interface BrandOption {
  name: string;
  slug: string;
}

export interface CatalogueUpdate {
  brand?: string;
  name?: string;
  description?: string;
  sort_order?: number;
}

export interface ExtractPositionsInput {
  startPage?: number;
  endPage?: number;
  dryRun?: boolean;
  includeInactive?: boolean;
}

export interface ExtractPositionsResult {
  matches: number;
  products: number;
  pagesProcessed: number;
  pagesWithText: number;
  totalPages: number;
  skus: number;
  ambiguousSkus: number;
  unmatchedSkus: number;
  brand: string;
  brandMethod: 'canonical' | 'like';
  scope: string;
  dryRun: boolean;
}

export interface ExtractionProgress {
  page: number;
  startPage: number;
  endPage: number;
  totalPages: number;
  matches: number;
}

export interface ExtractionJob {
  catalogueId: string;
  status: 'running' | 'done' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  progress: ExtractionProgress | null;
  result: ExtractPositionsResult | null;
  error: string | null;
}

const BASE = '/api/v1/catalogues';

// Multipart upload can't go through the JSON apiClient — do a raw fetch, letting
// the browser set the multipart boundary. Auth token is read the same way apiClient does.
async function uploadMultipart(form: FormData): Promise<Catalogue> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE_URL}${BASE}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  const json = await res.json();
  return json.data;
}

export const catalogueService = {
  async list(): Promise<{ catalogues: Catalogue[]; brands: BrandOption[] }> {
    const res = await api.get<{ data: Catalogue[]; brands: BrandOption[] }>(BASE);
    return { catalogues: res.data, brands: res.brands ?? [] };
  },

  async upload(input: { file: File; brand: string; name: string; description?: string; sort_order?: number }): Promise<Catalogue> {
    const form = new FormData();
    form.append('file', input.file);
    form.append('brand', input.brand);
    form.append('name', input.name);
    if (input.description) form.append('description', input.description);
    if (input.sort_order !== undefined) form.append('sort_order', String(input.sort_order));
    return uploadMultipart(form);
  },

  async update(id: string, data: CatalogueUpdate): Promise<Catalogue> {
    const res = await api.put<{ data: Catalogue }>(`${BASE}/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async remove(id: string): Promise<Catalogue> {
    const res = await api.delete<{ data: Catalogue }>(`${BASE}/${encodeURIComponent(id)}`);
    return res.data;
  },

  async reorder(items: { id: string; sort_order: number }[]): Promise<void> {
    await api.post(`${BASE}/reorder`, items);
  },

  async pageCount(id: string): Promise<number> {
    const res = await api.get<{ data: { pages: number } }>(`${BASE}/${encodeURIComponent(id)}/page-count`);
    return res.data.pages;
  },

  // Starts the job (202). The server parses the whole range in memory and publishes in one
  // transaction, so nothing is lost if it fails part-way. Poll extractStatus() for progress.
  async extractPositions(id: string, input: ExtractPositionsInput = {}): Promise<ExtractionJob> {
    const res = await api.post<{ data: ExtractionJob }>(
      `${BASE}/${encodeURIComponent(id)}/extract-positions`,
      input
    );
    return res.data;
  },

  async extractStatus(id: string): Promise<ExtractionJob> {
    const res = await api.get<{ data: ExtractionJob }>(`${BASE}/${encodeURIComponent(id)}/extract-status`);
    return res.data;
  },

  // Start + poll until the job finishes. Resolves with the summary, rejects on failure.
  async runExtraction(
    id: string,
    input: ExtractPositionsInput = {},
    onProgress?: (job: ExtractionJob) => void,
    intervalMs = 1500
  ): Promise<ExtractPositionsResult> {
    let job = await this.extractPositions(id, input);
    onProgress?.(job);
    while (job.status === 'running') {
      await new Promise((r) => setTimeout(r, intervalMs));
      job = await this.extractStatus(id);
      onProgress?.(job);
    }
    if (job.status === 'failed' || !job.result) throw new Error(job.error || 'Extraction failed');
    return job.result;
  },

  async extractCover(id: string): Promise<Catalogue> {
    const res = await api.post<{ data: Catalogue }>(`${BASE}/${encodeURIComponent(id)}/extract-cover`);
    return res.data;
  },
};
